/**
 * Service Worker Registration with Smart Update Detection
 * Ensures users always get the latest version
 */

const APP_VERSION = process.env.REACT_APP_VERSION || Date.now().toString();

export const register = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {
          console.log('✅ Service Worker registered');
          console.log('📦 App Version:', APP_VERSION);

          // Check for updates immediately and every 30 seconds
          const checkForUpdates = () => {
            registration.update().catch(err => {
              console.warn('Update check failed:', err);
            });
          };
          
          checkForUpdates();
          setInterval(checkForUpdates, 30 * 1000); // Check every 30 seconds

          // Listen for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            console.log('🔄 New version detected, installing...');
            
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  // New service worker available
                  console.log('✨ New version ready');
                  showUpdateNotification();
                } else {
                  // First install
                  console.log('✅ App ready for offline use');
                }
              }
            });
          });
        })
        .catch((error) => {
          console.error('❌ Service Worker registration failed:', error);
        });

      // Listen for version updates from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'VERSION_UPDATE') {
          console.log('📦 Version updated to:', event.data.version);
          localStorage.setItem('app_version', event.data.version);
        }
      });

      // Handle controller change (new service worker activated)
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          console.log('🔄 New service worker activated, reloading...');
          refreshing = true;
          window.location.reload();
        }
      });
    });
  }
};

// Show update notification to user
function showUpdateNotification() {
  // Check if we should auto-reload or show notification
  const lastReload = localStorage.getItem('last_auto_reload');
  const now = Date.now();
  
  // Auto-reload if last reload was more than 5 minutes ago
  if (!lastReload || (now - parseInt(lastReload)) > 5 * 60 * 1000) {
    console.log('🔄 Auto-reloading to apply update...');
    localStorage.setItem('last_auto_reload', now.toString());
    
    // Skip waiting and reload
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Reload after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  } else {
    // Show banner if auto-reload happened recently
    showUpdateBanner();
  }
}

// Show update banner (built with DOM API instead of innerHTML to avoid
// any chance of script injection — banner content is fully static and
// safe, but using textContent + appendChild is the lint-clean pattern).
function showUpdateBanner() {
  const wrapper = document.createElement('div');
  wrapper.id = 'update-banner';

  const inner = document.createElement('div');
  inner.style.cssText =
    'position:fixed;top:0;left:0;right:0;' +
    'background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);' +
    'color:white;padding:12px 20px;text-align:center;z-index:10000;' +
    'box-shadow:0 2px 8px rgba(0,0,0,0.15);' +
    "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;" +
    'display:flex;align-items:center;justify-content:center;gap:16px;';

  const label = document.createElement('span');
  label.style.fontWeight = '500';
  label.textContent = '✨ New version available!';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Refresh Now';
  btn.style.cssText =
    'background:white;color:#667eea;border:none;padding:6px 16px;' +
    'border-radius:20px;font-weight:600;cursor:pointer;font-size:14px;';
  btn.addEventListener('click', () => window.location.reload());

  inner.appendChild(label);
  inner.appendChild(btn);
  wrapper.appendChild(inner);
  document.body.appendChild(wrapper);
}

export const unregister = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
        console.log('🗑️ Service Worker unregistered');
      })
      .catch((error) => {
        console.error('Unregister failed:', error);
      });
  }
};

// Clear all caches and unregister service worker
export const clearAllCaches = async () => {
  if ('serviceWorker' in navigator) {
    // Unregister service worker
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    
    console.log('🗑️ All caches cleared and service worker unregistered');
    return true;
  }
  return false;
};

// Check if service worker is supported
export const isSupported = () => {
  return 'serviceWorker' in navigator;
};

// Get app version
export const getAppVersion = () => {
  return APP_VERSION;
};

// Get service worker version
export const getServiceWorkerVersion = async () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data.version);
      };
      navigator.serviceWorker.controller.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    });
  }
  return null;
};

export default { 
  register, 
  unregister, 
  clearAllCaches,
  isSupported, 
  getAppVersion,
  getServiceWorkerVersion 
};
